import type { SdkConfig } from './types';
import { readCookie, writeCookie, deleteCookie, inferCookieDomain } from './storage';
import { uuid, nowMs, hashCode } from './utils';
import { hashEmail, isValidEmail } from './email';
import { looksLikePII } from './redact';
import { warn, log } from './log';
import type { Consent } from './consent';

const COOKIE_EMBTRK_V2 = '_embtrk_v2';
const COOKIE_EMBTRK_LEGACY = '_embtrk';
const COOKIE_EMBTRK_SUPER_LEGACY = 'embtrk';
const COOKIE_ANON_ID = '_emb_anon_id';
const COOKIE_SESSION = '_emb_session';
const COOKIE_EMAIL_HASH = '_emb_email_h';
const COOKIE_EXTERNAL_ID = '_emb_uid';
const COOKIE_CLICK_HANDLE = '_emb_h';
const COOKIE_CONTACT_REF = '_emb_cr';
const COOKIE_SPENT_HANDLE = '_emb_hs';

/** Query parameter clicks-proxy puts on the redirect that brought this visitor
 *  here. Duplicated from `common/identity/click-handoff.ts` — the SDK is a
 *  separate build with nothing to import. Change one, change the other. */
const CLICK_HANDLE_PARAM = 'emb_h';

/**
 * The handle is a landing-page artifact: it is sent on the next batch and then
 * replaced by the hashed identity the edge hands back. An hour matches the token's
 * own validity window, so a stale cookie can only ever hold a token the edge would
 * refuse anyway.
 */
const CLICK_HANDLE_TTL_S = 60 * 60;

const SHA256_HEX = /^[0-9a-f]{64}$/i;

/** The edge rejects anything longer, so persisting it would only grow the
 *  cookie for a value that can never be sent. */
const MAX_EXTERNAL_ID_LENGTH = 128;

const SIXTY_DAYS = 60 * 24 * 60 * 60;
const SESSION_TTL_MS = 30 * 60 * 1000;
const SESSION_TTL_S = 30 * 60;

export type EmailListener = (emailSha256: string) => void;

export class Identity {
	anonId: string | null = null;
	sessionId: string | null = null;
	/** True only on the page-load that minted the current session_id (no prior
	 *  cookie). Consumers read it once at boot to fire session.start. */
	sessionIsNew = false;
	embtrk: string | null = null;
	externalUserId: string | null = null;
	emailSha256: string | null = null;
	/**
	 * Encrypted identity handed over by clicks-proxy on the redirect the visitor
	 * followed to get here. Opaque: the SDK cannot read it and never tries. Sent
	 * with the batch until the edge answers with the hashed identity, then dropped.
	 */
	clickHandle: string | null = null;
	/** Non-reversible contact identifier the edge resolved for this visitor. The
	 *  identity that survives after the handle is spent. */
	contactRef: string | null = null;
	traits: Record<string, unknown> = {};
	private sessionExpiresAt = 0;
	private cookieDomain: string | undefined;
	private emailListeners: EmailListener[] = [];

	constructor(private config: SdkConfig, private consent: Consent) {
		this.cookieDomain = config.cookieDomain || inferCookieDomain();
	}

	init(): void {
		// 1. embtrk migration legacy → v2 — copy without losing the value so
		// tokens in circulation keep matching, then consume the query string.
		const legacyToken = readCookie(COOKIE_EMBTRK_LEGACY) || readCookie(COOKIE_EMBTRK_SUPER_LEGACY);
		const v2Token = readCookie(COOKIE_EMBTRK_V2);
		const queryToken = this.readQueryEmbtrk();

		const resolved = queryToken || v2Token || legacyToken || null;
		this.embtrk = resolved;

		if (queryToken && this.consent.canSetCookies()) {
			this.writeEmbtrk(queryToken);
		} else if (legacyToken && !v2Token && this.consent.canSetCookies()) {
			this.writeEmbtrk(legacyToken);
		}

		// 2. anon_id — only set when consent allows.
		this.anonId = readCookie(COOKIE_ANON_ID);
		if (!this.anonId && this.consent.canSetCookies()) {
			this.anonId = uuid();
			writeCookie(COOKIE_ANON_ID, this.anonId, {
				maxAgeSeconds: SIXTY_DAYS,
				domain: this.cookieDomain,
			});
		}

		// 3. session
		this.refreshSession();

		// 4. email hash — persisted form of identity. Cleartext is never stored.
		this.emailSha256 = readCookie(COOKIE_EMAIL_HASH);

		// 5. external user id from a previous identify(). Persisted so a visitor who
		// logged in on another page is still identified here: without it the envelope
		// loses external_user_id on the next navigation and the opt-in ledger can't
		// tell a returning contact from a new one.
		this.externalUserId = readCookie(COOKIE_EXTERNAL_ID);

		// 6. click handoff. The query wins over the cookie: a fresh click supersedes
		// whatever an earlier one left behind.
		this.initClickHandoff();
	}

	/**
	 * Picks up the handoff token from the landing URL and takes it out of the URL.
	 *
	 * Removing it matters as much as reading it. The token would otherwise sit in
	 * the customer's address bar, get copied into their analytics as part of the
	 * page URL, and leak to every third party that reads `document.referrer` on the
	 * next navigation. It is single-use identity: it belongs in one POST to the
	 * edge, nowhere else.
	 */
	private initClickHandoff(): void {
		const fromQuery = this.readQueryParam(CLICK_HANDLE_PARAM);
		const stored = readCookie(COOKIE_CLICK_HANDLE);

		this.clickHandle = fromQuery || stored || null;

		// A handle we already redeemed must never be sent twice. The token lives in
		// the landing URL, so re-opening that URL from history — or from a bookmark —
		// would otherwise hand the edge the same identity again, and the edge would
		// answer by emitting a second, identical mapping row for the lake.
		if (this.clickHandle && hashCode(this.clickHandle) === readCookie(COOKIE_SPENT_HANDLE)) {
			log('identity: click handle already redeemed, ignoring');
			this.clickHandle = null;
			deleteCookie(COOKIE_CLICK_HANDLE, { domain: this.cookieDomain });
			this.contactRef = readCookie(COOKIE_CONTACT_REF);
			if (fromQuery) stripQueryParam(CLICK_HANDLE_PARAM);
			return;
		}

		if (fromQuery && this.consent.canSetCookies()) {
			writeCookie(COOKIE_CLICK_HANDLE, fromQuery, {
				maxAgeSeconds: CLICK_HANDLE_TTL_S,
				domain: this.cookieDomain,
			});
		}
		if (fromQuery) stripQueryParam(CLICK_HANDLE_PARAM);

		this.contactRef = readCookie(COOKIE_CONTACT_REF);
	}

	/**
	 * Applies the hashed identity the edge resolved from the handle.
	 *
	 * Deliberately silent: no `optin` fires from here. The Worker already emitted
	 * that row for this exact event — it is the only party that holds the numeric
	 * contact id, so it is the one that can state the mapping — and firing a second
	 * one from the browser would double-count the same opt-in.
	 *
	 * Returns true when something changed, so the queue can log it.
	 */
	absorbHandback(handback: { email_sha256?: unknown; contact_ref?: unknown } | undefined): boolean {
		if (!handback || typeof handback !== 'object') return false;

		let changed = false;
		let resolved = false;

		const hash = handback.email_sha256;
		if (typeof hash === 'string' && SHA256_HEX.test(hash)) resolved = true;
		if (typeof handback.contact_ref === 'string' && handback.contact_ref) resolved = true;

		if (typeof hash === 'string' && SHA256_HEX.test(hash) && hash.toLowerCase() !== this.emailSha256) {
			this.emailSha256 = hash.toLowerCase();
			if (this.consent.canSetCookies()) {
				writeCookie(COOKIE_EMAIL_HASH, this.emailSha256, {
					maxAgeSeconds: SIXTY_DAYS,
					domain: this.cookieDomain,
				});
			}
			changed = true;
		}

		const ref = handback.contact_ref;
		if (typeof ref === 'string' && ref && ref !== this.contactRef) {
			this.contactRef = ref;
			if (this.consent.canSetCookies()) {
				writeCookie(COOKIE_CONTACT_REF, ref, {
					maxAgeSeconds: SIXTY_DAYS,
					domain: this.cookieDomain,
				});
			}
			changed = true;
		}

		// Spent on `resolved`, not on `changed`: a returning visitor who clicks a new
		// campaign gets an identity they already had, so nothing changes locally — but
		// the handle was still redeemed, and sending it again would make the edge emit
		// another mapping row.
		if (resolved) this.consumeClickHandle();

		return changed;
	}

	private consumeClickHandle(): void {
		// Remembered for exactly as long as a token can still be opened by the edge:
		// past that window the edge rejects it anyway, so the fingerprint has nothing
		// left to protect.
		if (this.clickHandle && this.consent.canSetCookies()) {
			writeCookie(COOKIE_SPENT_HANDLE, hashCode(this.clickHandle), {
				maxAgeSeconds: CLICK_HANDLE_TTL_S,
				domain: this.cookieDomain,
			});
		}
		this.clickHandle = null;
		deleteCookie(COOKIE_CLICK_HANDLE, { domain: this.cookieDomain });
	}

	private readQueryParam(name: string): string | null {
		if (typeof location === 'undefined' || !location.search) return null;
		try {
			return new URLSearchParams(location.search).get(name);
		} catch {
			const m = location.search.match(new RegExp('[?&]' + name + '=([^&]+)'));
			return m ? decodeURIComponent(m[1]) : null;
		}
	}

	private readQueryEmbtrk(): string | null {
		return this.readQueryParam('embtrk');
	}

	private writeEmbtrk(value: string): void {
		writeCookie(COOKIE_EMBTRK_V2, value, {
			maxAgeSeconds: SIXTY_DAYS,
			domain: this.cookieDomain,
		});
		this.embtrk = value;
	}

	refreshSession(): void {
		const now = nowMs();
		const existing = readCookie(COOKIE_SESSION);
		if (existing && this.sessionExpiresAt > now) {
			this.sessionId = existing;
			this.sessionExpiresAt = now + SESSION_TTL_MS;
		} else {
			if (!existing) this.sessionIsNew = true;
			this.sessionId = existing || uuid();
			this.sessionExpiresAt = now + SESSION_TTL_MS;
		}
		if (this.consent.canSetCookies()) {
			writeCookie(COOKIE_SESSION, this.sessionId, {
				maxAgeSeconds: SESSION_TTL_S,
				domain: this.cookieDomain,
			});
		}
	}

	identify(externalUserId: string, traits?: Record<string, unknown>): void {
		this.externalUserId = externalUserId;
		if (this.consent.canSetCookies() && externalUserId.length <= MAX_EXTERNAL_ID_LENGTH) {
			writeCookie(COOKIE_EXTERNAL_ID, externalUserId, {
				maxAgeSeconds: SIXTY_DAYS,
				domain: this.cookieDomain,
			});
		}
		if (traits) {
			for (const [k, v] of Object.entries(traits)) {
				// Email is handled separately (hashed + persisted) — callers
				// use api.identify() which extracts traits.email before reaching here.
				if (k === 'email') continue;
				if (looksLikePII(v)) {
					warn(`identify(): trait "${k}" looks like PII (phone/cc). Hash it before sending.`);
					continue;
				}
				this.traits[k] = v;
			}
		}
		log('identify', { externalUserId, traits: this.traits });
	}

	/**
	 * Set the contact's email by hashing it client-side. Idempotent: if we
	 * already have the same hash, this is a no-op. Persists in cookie so
	 * subsequent page views start identified without re-hashing.
	 *
	 * Returns the resulting hash (or null if the input was invalid or
	 * SubtleCrypto wasn't available).
	 */
	async setEmail(cleartext: string): Promise<string | null> {
		if (!isValidEmail(cleartext)) return null;
		const hash = await hashEmail(cleartext);
		if (!hash) return null;
		if (hash === this.emailSha256) return hash;
		this.emailSha256 = hash;
		if (this.consent.canSetCookies()) {
			writeCookie(COOKIE_EMAIL_HASH, hash, {
				maxAgeSeconds: SIXTY_DAYS,
				domain: this.cookieDomain,
			});
		}
		log('identity: email hash set', { hash });
		for (const cb of this.emailListeners.slice()) {
			try {
				cb(hash);
			} catch (e) {
				log('email listener error', e);
			}
		}
		return hash;
	}

	onEmailChange(cb: EmailListener): void {
		this.emailListeners.push(cb);
	}

	reset(): void {
		this.anonId = null;
		this.sessionId = null;
		this.embtrk = null;
		this.externalUserId = null;
		this.emailSha256 = null;
		this.contactRef = null;
		this.traits = {};
		deleteCookie(COOKIE_ANON_ID, { domain: this.cookieDomain });
		deleteCookie(COOKIE_SESSION, { domain: this.cookieDomain });
		deleteCookie(COOKIE_EMBTRK_V2, { domain: this.cookieDomain });
		deleteCookie(COOKIE_EMAIL_HASH, { domain: this.cookieDomain });
		deleteCookie(COOKIE_EXTERNAL_ID, { domain: this.cookieDomain });
		deleteCookie(COOKIE_CONTACT_REF, { domain: this.cookieDomain });
		deleteCookie(COOKIE_SPENT_HANDLE, { domain: this.cookieDomain });
		this.clickHandle = null;
		deleteCookie(COOKIE_CLICK_HANDLE, { domain: this.cookieDomain });
	}
}

/**
 * Removes a parameter from the address bar without navigating or adding a history
 * entry. Best-effort: no History API, or a URL we can't parse, and the parameter
 * simply stays — it has already been read into memory either way.
 */
function stripQueryParam(name: string): void {
	if (typeof history === 'undefined' || typeof history.replaceState !== 'function') return;
	if (typeof location === 'undefined') return;

	try {
		const url = new URL(location.href);
		if (!url.searchParams.has(name)) return;
		url.searchParams.delete(name);
		// `search` keeps the "?" only while there is something after it, so a URL
		// whose only parameter was ours comes out clean rather than with a trailing
		// question mark.
		history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
	} catch {
		// Leave the URL alone.
	}
}
